import { Link } from 'react-router-dom';
import {
  useListAdminUsers,
  type AdminUser
} from '@cdf/api-client-react';
import {
  ArrowUpRightIcon,
  ClipboardListIcon,
  PhoneIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldIcon,
  UsersIcon
} from 'lucide-react';
import { useListPhoneApprovals } from '@cdf/api-client-react';

import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

type AdminCard = {
  title: string;
  description: string;
  to: string;
  icon: typeof UsersIcon;
  stat?: string;
  statLabel?: string;
};

export function AdminHomePage() {
  const { data, isLoading, isError } = useListAdminUsers();
  const { data: phoneApprovals } = useListPhoneApprovals({ status: 'pending' });
  const users = data?.users ?? [];
  const total = data?.total ?? users.length;
  const pending = users.filter((u: AdminUser) => u.role === 'pending').length;
  const inactive = users.filter((u: AdminUser) => !u.isActive).length;
  const pendingNumbers = phoneApprovals?.total ?? 0;

  const cards: AdminCard[] = [
    {
      title: 'User management',
      description: 'Assign roles, activate accounts, and review last sign-in.',
      to: '/admin/users',
      icon: UsersIcon,
      stat: isLoading ? undefined : String(total),
      statLabel: pending > 0 ? `${pending} awaiting access` : 'synced users'
    },
    {
      title: 'Admin audit log',
      description: 'Review administrative events across the workspace.',
      to: '/admin/audit-log',
      icon: ScrollTextIcon,
      statLabel: 'activity trail'
    },
    {
      title: 'Settings',
      description: 'General workspace preferences and appearance.',
      to: '/settings',
      icon: SettingsIcon,
      statLabel: 'configuration'
    },
    {
      title: 'Attendance register',
      description: 'Configure how teachers record and review event attendance.',
      to: '/settings/attendance',
      icon: ClipboardListIcon,
      statLabel: 'event checkout'
    },
    {
      title: 'Approved numbers',
      description: 'Approve unknown SMS senders and manage guardian phone numbers.',
      to: '/approved-numbers',
      icon: PhoneIcon,
      stat: pendingNumbers > 0 ? String(pendingNumbers) : undefined,
      statLabel: pendingNumbers > 0 ? 'need approval' : 'SMS guardians'
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Administration"
        description="Manage users, roles, and workspace configuration."
        icon={ShieldIcon}
      />

      {!isLoading && !isError && pending > 0 && (
        <Card className="border-border/80">
          <CardContent className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {pending} user{pending === 1 ? '' : 's'} awaiting access
              </p>
              <p className="text-muted-foreground text-sm">
                New sign-ins start as pending until an admin assigns a role.
                {inactive > 0 ? ` ${inactive} account${inactive === 1 ? '' : 's'} deactivated.` : ''}
              </p>
            </div>
            <Link
              to="/admin/users"
              className="text-sm font-medium underline-offset-4 hover:underline"
            >
              Review users
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Link key={card.to} to={card.to} className="group block focus-visible:outline-none">
              <Card className="border-border/80 h-full transition-colors group-hover:border-foreground/30 group-focus-visible:ring-ring group-focus-visible:ring-2">
                <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                  <div className="bg-muted text-foreground flex size-10 items-center justify-center rounded-md">
                    <Icon className="size-5" aria-hidden />
                  </div>
                  <ArrowUpRightIcon
                    className="text-muted-foreground size-4 opacity-0 transition-opacity group-hover:opacity-100"
                    aria-hidden
                  />
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1">
                    <CardTitle className="text-base">{card.title}</CardTitle>
                    <CardDescription>{card.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {isLoading && card.to === '/admin/users' ? (
                      <Skeleton className="h-6 w-16" />
                    ) : card.stat !== undefined ? (
                      <span className="text-2xl font-semibold tabular-nums">{card.stat}</span>
                    ) : null}
                    {card.statLabel ? (
                      <Badge variant="outline" className="font-normal">
                        {card.statLabel}
                      </Badge>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
